'use client';

import { createInstance } from 'i18next';
import { useEffect, useState } from 'react';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next, I18nextProvider as Provider } from 'react-i18next';
import { i18nOptions, fallbackLng } from './config-locales';

import arCommon from './langs/ar/common.json';
import arAgents from './langs/ar/agents.json';
import arCampaigns from './langs/ar/campaigns.json';
import arDashboard from './langs/ar/dashboard.json';
import arNavbar from './langs/ar/navbar.json';
import arPayroll from './langs/ar/payroll.json';
import arReports from './langs/ar/reports.json';
import arRuns from './langs/ar/runs.json';
import arSettings from './langs/ar/settings.json';
import enCommon from './langs/en/common.json';
import enAgents from './langs/en/agents.json';
import enCampaigns from './langs/en/campaigns.json';
import enDashboard from './langs/en/dashboard.json';
import enNavbar from './langs/en/navbar.json';
import enOsa from './langs/en/osa.json';
import enPayroll from './langs/en/payroll.json';
import enReports from './langs/en/reports.json';
import enRuns from './langs/en/runs.json';
import enSettings from './langs/en/settings.json';
import enWallet from './langs/en/wallet.json';
import frCommon from './langs/fr/common.json';
import frAgents from './langs/fr/agents.json';
import frCampaigns from './langs/fr/campaigns.json';
import frDashboard from './langs/fr/dashboard.json';
import frNavbar from './langs/fr/navbar.json';
import frPayroll from './langs/fr/payroll.json';
import frReports from './langs/fr/reports.json';
import frRuns from './langs/fr/runs.json';
import frSettings from './langs/fr/settings.json';
import frWallet from './langs/fr/wallet.json';
import rwCommon from './langs/rw/common.json';
import rwAgents from './langs/rw/agents.json';
import rwCampaigns from './langs/rw/campaigns.json';
import rwDashboard from './langs/rw/dashboard.json';
import rwNavbar from './langs/rw/navbar.json';
import rwPayroll from './langs/rw/payroll.json';
import rwReports from './langs/rw/reports.json';
import rwRuns from './langs/rw/runs.json';
import rwSettings from './langs/rw/settings.json';
import rwWallet from './langs/rw/wallet.json';
import swCommon from './langs/sw/common.json';
import swAgents from './langs/sw/agents.json';
import swCampaigns from './langs/sw/campaigns.json';
import swDashboard from './langs/sw/dashboard.json';
import swNavbar from './langs/sw/navbar.json';
import swOsa from './langs/sw/osa.json';
import swPayroll from './langs/sw/payroll.json';
import swReports from './langs/sw/reports.json';
import swRuns from './langs/sw/runs.json';
import swSettings from './langs/sw/settings.json';
import swWallet from './langs/sw/wallet.json';

import type { LanguageValue } from './config-locales';

// ----------------------------------------------------------------------

const resources = {
  ar: { agents: arAgents, campaigns: arCampaigns, common: arCommon, dashboard: arDashboard, navbar: arNavbar, payroll: arPayroll, reports: arReports, runs: arRuns, settings: arSettings },
  en: { agents: enAgents, campaigns: enCampaigns, common: enCommon, dashboard: enDashboard, navbar: enNavbar, osa: enOsa, payroll: enPayroll, reports: enReports, runs: enRuns, settings: enSettings, wallet: enWallet },
  fr: { agents: frAgents, campaigns: frCampaigns, common: frCommon, dashboard: frDashboard, navbar: frNavbar, payroll: frPayroll, reports: frReports, runs: frRuns, settings: frSettings, wallet: frWallet },
  rw: { agents: rwAgents, campaigns: rwCampaigns, common: rwCommon, dashboard: rwDashboard, navbar: rwNavbar, payroll: rwPayroll, reports: rwReports, runs: rwRuns, settings: rwSettings, wallet: rwWallet },
  sw: { agents: swAgents, campaigns: swCampaigns, common: swCommon, dashboard: swDashboard, navbar: swNavbar, osa: swOsa, payroll: swPayroll, reports: swReports, runs: swRuns, settings: swSettings, wallet: swWallet },
};

// ----------------------------------------------------------------------

type Props = {
  lang?: LanguageValue;
  children: React.ReactNode;
};

export function I18nProvider({ lang = fallbackLng, children }: Props) {
  const [i18n] = useState(() => {
    const instance = createInstance();

    instance
      .use(initReactI18next)
      .use(resourcesToBackend((language: string, ns: string) => import(`./langs/${language}/${ns}.json`)))
      .init({
        ...i18nOptions(lang),
        lng: lang,
        resources,
        initImmediate: false,
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
      });

    return instance;
  });

  useEffect(() => {
    if (i18n.resolvedLanguage !== lang) {
      i18n.changeLanguage(lang);
    }

    try {
      window.localStorage.setItem('i18nextLng', lang);
    } catch (_) {
      // Storage may be unavailable in some environments.
    }
  }, [i18n, lang]);

  return <Provider i18n={i18n}>{children}</Provider>;
}
